/**
 * Pages/Panel/Empleados/curp.js
 *
 * Cálculo de la CURP a partir de los datos de la persona, según el Instructivo Normativo para la
 * Asignación de la CURP de RENAPO. Lo usa la calculadora del campo CURP (CalculadoraCurp.js) en
 * Agregar Empleado, Editar Empleado y el alta por pasos.
 *
 * Los primeros 16 caracteres salen de los datos. El 17 (homoclave) lo asigna RENAPO para distinguir
 * a dos personas con los mismos datos: aquí se propone el de la primera, "0" si nació antes de 2000
 * y "A" desde 2000, y el 18 es el dígito verificador de los 17 anteriores. Por eso una CURP calculada
 * se confirma en gob.mx/curp.
 *
 * Módulo puro (sin React ni store): se puede probar con un script de Node.
 */

import moment from "moment";
import { ESTADOS_RENAPO, normalizarTexto } from "./ImportarEmpleadosExterno/mapeoExterno";

export const URL_CONSULTA_CURP = "https://www.gob.mx/curp/";

/** Entidades de nacimiento con su clave RENAPO (las de las posiciones 12 y 13), más NE. */
export const ENTIDADES_CURP = Object.entries(ESTADOS_RENAPO).map(([clave, nombres]) => ({
  clave,
  nombre: nombres[0] || "Nacido en el extranjero",
}));

/** Tramos de la CURP, para pintarla separada en la calculadora. */
export const SEGMENTOS_CURP = [
  { desde: 0, hasta: 4, titulo: "Iniciales" },
  { desde: 4, hasta: 10, titulo: "Nacimiento" },
  { desde: 10, hasta: 11, titulo: "Sexo" },
  { desde: 11, hasta: 13, titulo: "Entidad" },
  { desde: 13, hasta: 16, titulo: "Consonantes" },
  { desde: 16, hasta: 18, titulo: "Homoclave" },
];

/** Los únicos valores de `Empleado.sexo` que tienen letra en la CURP ("No Aplica" no la tiene). */
export const SEXO_CURP = { Hombre: "H", Mujer: "M" };

// Partículas que no cuentan en apellidos ni en nombres compuestos: "De la Cruz" se toma como CRUZ.
// También las usa el RFC (rfc.js).
export const PARTICULAS = new Set([
  "DA", "DAS", "DE", "DEL", "DER", "DI", "DIE", "DD", "EL", "LA", "LOS", "LAS", "LE", "LES",
  "MAC", "MC", "VAN", "VON", "Y",
]);

// Cuando un nombre compuesto empieza con uno de éstos se usa el siguiente: "María Fernanda" va con F.
export const NOMBRES_QUE_SE_BRINCAN = new Set(["MARIA", "MA", "M", "JOSE", "J"]);

// Anexo 2 del instructivo: si las cuatro primeras letras forman una de estas palabras, la segunda
// se cambia por X (PUTA → PXTA). El RFC hace lo mismo pero con la ÚLTIMA letra, por eso los dos no
// siempre empatan en sus primeros caracteres.
const ALTISONANTES = new Set([
  "BACA", "BAKA", "BUEI", "BUEY", "CACA", "CACO", "CAGA", "CAGO", "CAKA", "CAKO", "COGE", "COGI",
  "COJA", "COJE", "COJI", "COJO", "COLA", "CULO", "FALO", "FETO", "GETA", "GUEI", "GUEY", "JETA",
  "JOTO", "KACA", "KACO", "KAGA", "KAGO", "KAKA", "KAKO", "KOGE", "KOGI", "KOJA", "KOJE", "KOJI",
  "KOJO", "KOLA", "KULO", "LILO", "LOCA", "LOCO", "LOKA", "LOKO", "MAME", "MAMO", "MEAR", "MEAS",
  "MEON", "MIAR", "MION", "MOCO", "MOKO", "MULA", "MULO", "NACA", "NACO", "PEDA", "PEDO", "PENE",
  "PIPI", "PITO", "POPO", "PUTA", "PUTO", "QULO", "RATA", "ROBA", "ROBE", "ROBO", "RUIN", "SENO",
  "TETA", "VACA", "VAGA", "VAGO", "VAKA", "VUEI", "VUEY", "WUEI", "WUEY",
]);

// Valor de cada carácter para el dígito verificador (la Ñ conserva su lugar aunque la CURP no la use).
const DICCIONARIO_DIGITO = "0123456789ABCDEFGHIJKLMNÑOPQRSTUVWXYZ";
const DIACRITICOS = /[̀-ͯ]/g;

const esVocal = (c) => "AEIOU".includes(c);
const esConsonante = (c) => !esVocal(c);

// Mayúsculas, sin acentos y con la Ñ como X (la CURP no lleva Ñ). La Ñ se cambia ANTES de quitar
// los acentos: NFD la parte en N + tilde y se quedaría como N. Guiones y diagonales separan
// palabras; los demás símbolos (puntos de "Ma.", apóstrofos) se quitan.
const palabras = (texto) => String(texto ?? "")
  .toUpperCase()
  .replace(/Ñ/g, "X")
  .normalize("NFD")
  .replace(DIACRITICOS, "")
  .replace(/[-/]/g, " ")
  .replace(/[^A-Z ]/g, "")
  .split(/\s+/)
  .filter(Boolean);

// La palabra que cuenta de un apellido: la primera que no es partícula.
const palabraApellido = (texto) => {
  const todas = palabras(texto);
  return todas.find((p) => !PARTICULAS.has(p)) || todas[0] || "";
};

// La palabra que cuenta del nombre: sin partículas, y brincando María/José si hay otro nombre.
const palabraNombre = (texto) => {
  const todas = palabras(texto);
  const utiles = todas.filter((p) => !PARTICULAS.has(p));
  if (utiles.length > 1 && NOMBRES_QUE_SE_BRINCAN.has(utiles[0])) return utiles[1];
  return utiles[0] || todas[0] || "";
};

// Primera letra INTERNA (después de la inicial) que cumpla la condición; X si no hay.
const primeraInterna = (palabra, cumple) => {
  for (let i = 1; i < palabra.length; i++) if (cumple(palabra[i])) return palabra[i];
  return "X";
};

// Acepta el moment del DatePicker, un Date o "YYYY-MM-DD". El moment se lee en hora local: es el
// mismo día que se ve en el campo.
export const leerFecha = (fecha) => {
  if (!fecha) return null;
  const m = moment.isMoment(fecha)
    ? fecha
    : typeof fecha === "string" && /^\d{4}-\d{2}-\d{2}$/.test(fecha) ? moment(fecha, "YYYY-MM-DD") : moment(fecha);
  return m.isValid() && m.year() >= 1900 ? m : null;
};

/** Dígito verificador (posición 18) de los primeros 17 caracteres. */
export function digitoVerificador(curp17) {
  let suma = 0;
  for (let i = 0; i < 17; i++) suma += DICCIONARIO_DIGITO.indexOf(curp17[i]) * (18 - i);
  return String((10 - (suma % 10)) % 10);
}

/**
 * Calcula la CURP. `sexo` es el del modelo ("Hombre" / "Mujer") y `entidad` la clave RENAPO de
 * ENTIDADES_CURP. Regresa `{ curp, faltan }`: sin todos los datos, `curp` es null y `faltan` dice
 * cuáles.
 */
export function calcularCurp({ nombre, apellidoPaterno, apellidoMaterno, fechaNacimiento, sexo, entidad } = {}) {
  const paterno = palabraApellido(apellidoPaterno);
  const materno = palabraApellido(apellidoMaterno);
  const nombrePila = palabraNombre(nombre);
  const fecha = leerFecha(fechaNacimiento);
  const letraSexo = SEXO_CURP[sexo];
  const claveEntidad = ESTADOS_RENAPO[entidad] ? entidad : null;

  const faltan = [];
  if (!nombrePila) faltan.push("Nombre(s)");
  if (!paterno) faltan.push("Apellido paterno");
  if (!fecha) faltan.push("Fecha de nacimiento");
  if (!letraSexo) faltan.push("Sexo");
  if (!claveEntidad) faltan.push("Entidad de nacimiento");
  if (faltan.length) return { curp: null, faltan };

  let iniciales = paterno[0] + primeraInterna(paterno, esVocal) + (materno[0] || "X") + nombrePila[0];
  if (ALTISONANTES.has(iniciales)) iniciales = iniciales[0] + "X" + iniciales.slice(2);

  const curp17 = iniciales
    + fecha.format("YYMMDD")
    + letraSexo
    + claveEntidad
    + primeraInterna(paterno, esConsonante)
    + (materno ? primeraInterna(materno, esConsonante) : "X")
    + primeraInterna(nombrePila, esConsonante)
    + (fecha.year() < 2000 ? "0" : "A");

  return { curp: curp17 + digitoVerificador(curp17), faltan };
}

/** ¿Las dos CURP son de los mismos datos? Compara los primeros 16 caracteres (sin la homoclave). */
export const mismaCurpBase = (a, b) => {
  const x = String(a ?? "").trim().toUpperCase();
  const y = String(b ?? "").trim().toUpperCase();
  return x.length === 18 && y.length === 18 && x.slice(0, 16) === y.slice(0, 16);
};

/**
 * Fecha de nacimiento que trae una CURP (posiciones 5 a 10, AAMMDD). El siglo lo da el carácter 17:
 * dígito para los nacidos antes de 2000 y letra desde 2000. `null` si no es una CURP o la fecha no
 * existe. La usa la calculadora de RFC cuando no se ha capturado la fecha.
 */
export const fechaDeCurp = (curp) => {
  const c = String(curp ?? "").trim().toUpperCase();
  if (!/^[A-Z]{4}\d{6}[HMX][A-Z]{5}[A-Z\d]\d$/.test(c)) return null;
  const siglo = /\d/.test(c[16]) ? "19" : "20";
  const m = moment(`${siglo}${c.slice(4, 10)}`, "YYYYMMDD", true);
  return m.isValid() ? m : null;
};

// Nombre normalizado → clave RENAPO, con todas las variantes de ESTADOS_RENAPO. Las variantes van
// de la más larga a la más corta para que "Baja California Sur" no se quede en "Baja California".
const VARIANTES_ESTADO = Object.entries(ESTADOS_RENAPO)
  .flatMap(([clave, nombres]) => nombres.map((n) => ({ clave, nombre: normalizarTexto(n) })))
  .sort((a, b) => b.nombre.length - a.nombre.length);

/**
 * Clave RENAPO de un documento del catálogo Estado, por su nombre ("Querétaro de Arteaga" empata
 * con "Querétaro"). `undefined` si no se reconoce: el catálogo también trae estados de otros
 * países, y su `abreviatura` no sirve para esto ("Ags.", y en la semilla viene desfasada).
 */
export const claveCurpDeEstado = (estado) => {
  const nombre = normalizarTexto(estado?.nombre);
  if (!nombre) return undefined;
  const exacta = VARIANTES_ESTADO.find((v) => v.nombre === nombre);
  if (exacta) return exacta.clave;
  return VARIANTES_ESTADO.find((v) => nombre.startsWith(`${v.nombre} `))?.clave;
};

/** El Estado del catálogo que corresponde a una clave RENAPO. `undefined` para NE o si no está. */
export const estadoDeClaveCurp = (clave, estados = []) => {
  if (!clave || clave === "NE") return undefined;
  return estados.find((e) => claveCurpDeEstado(e) === clave);
};
